import React, { useEffect, useState } from "react";
import ViewTable from "../../../Components/Common/ViewTable";
import { get, post, put, del, api } from "../../../utils/api";
import Cards from "../../../Components/Common/Cards";
import FilterModal from "../../../Components/Common/FilterModal";
import Form from "./Form";
import ActionButtons from "../../../Components/Common/ActionButtons";
import ViewData from "./ViewData.jsx";
import BulkUploadModal from "../../../Components/Common/BulkUploadModal";
import {
  Alert,
  Snackbar,
} from "@mui/material";
import { useUser } from "../../../context/AuthContext.js";
import DeleteAlertDialog from "../../../Components/Masters/DeleteAlertDialog.js";

const ProcessingOfSoldProducts = () => {
  const { user } = useUser();
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "" });
  const [tableData, setTableData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filterObj, setFilterObj] = useState({});
  const [selectedValue, setSelectedValue] = useState(null);
  const [bulkUploadModal, setBulkUploadModal] = useState(false);
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletedItemId, setDeletedItemId] = useState("");
  const [excelData, setExcelData] = useState(null);
  const [filters, setFilters] = useState({
    productName: [],
    productCategory: [],
    vendorName: [],
    locationOfProcessing: [],
  });

  const [cardData, setCardData] = useState({
    totalProcessings: 0,
    topCategory: [],
    topVendor: null,
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [snackOpen, setsnackOpen] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const headers = [
    { key: "createdAt", label: "Date", sortable: true },
    { key: "productName", label: "Product name", sortable: true },
    { key: "vendorName", label: "Customer/Vendor", sortable: true },
    { key: "quantitySoldDisplay", label: "Quantity sold", sortable: true },
    { key: "processingMethodUsed", label: "Processing method", sortable: true },
    { key: "energyDisplay", label: "Electricity (KWH)", sortable: true },
  ];

  const inputData = [
    { fieldName: "soldProductDate", title: "Sold Product Date" },
    { fieldName: "productId", title: "Product ID" },
    { fieldName: "productName", title: "Product Name" },
    { fieldName: "productCategory", title: "Product Category" },
    { fieldName: "vendorName", title: "Customer/Vendor Name" },
    { fieldName: "quantitySold", title: "Quantity Sold" },
    { fieldName: "locationOfProcessing", title: "Location of Processing" },
    { fieldName: "processingMethodUsed", title: "Processing Method Used" },
    { fieldName: "electricityConsumption", title: "Electricity Consumption (KWH)" },
    { fieldName: "fuelDisplay", title: "Fuel Consumption" },
    { fieldName: "materialsAddedDuringProcessing", title: "Materials Added During Processing" },
    { fieldName: "remark", title: "Remark (optional)" },
  ];

  // Find the category with maximum count
  const getTopCategory = () => {
    if (!Array.isArray(cardData?.topCategory) || cardData.topCategory.length === 0) {
      return "N/A";
    }
    
    // Find the category with the maximum count
    const topCategoryItem = cardData.topCategory.reduce((max, item) => {
      const itemCount = item?.count || 0;
      const maxCount = max?.count || 0;
      return itemCount > maxCount ? item : max;
    }, cardData.topCategory[0]);
    
    return topCategoryItem?.category || "N/A";
  };

  let bannerData = [
    {
      title: "Total Processings",
      description: `${cardData?.totalProcessings ?? 0} Processings`,
    },
    {
      title: "Top Product Category",
      description: getTopCategory(),
    },
    {
      title: "Top Vendor",
      description: cardData?.topVendor?.vendor || "N/A",
    },
  ];

  const filterData = [
    { title: "Date", type: "date", key: "createdAt", value: [] },
    {
      title: "Product Name",
      type: "option",
      key: "productName",
      value: filters?.productName,
    },
    {
      title: "Product Category",
      type: "option",
      key: "productCategory",
      value: filters?.productCategory,
    },
    {
      title: "Vendor Name",
      type: "option",
      key: "vendorName",
      value: filters?.vendorName,
    },
    {
      title: "Location of Processing",
      type: "option",
      key: "locationOfProcessing",
      value: filters?.locationOfProcessing || [],
    },
  ];

  const fetchExcelData = async () => {
    // Excel template with headers matching backend API expectations
    const template = [
      {
        "S No.": 1,
        "Product Name": "",
        "Product Category": "",
        "Vendor Name": "",
        "Quantity Sold": "",
        "Unit": "",
        "Location of Processing": "",
        "Processing Method Used": "",
        "Energy Consumption for Processing": "",
        "Energy Source for Processing": "",
        "Materials Added During Processing": "",
        "Waste Generated During Processing": "",
        "Water Used in Processing (Liters)": "",
        "Remark (optional)": "",
      },
    ];
    setExcelData(template);
  };

  useEffect(() => {
    fetchExcelData();
  }, []);

  const fetchData = async () => {
    let filter = { ...filterObj };
    if (filter.createdAt) {
      filter.startDate = filter?.createdAt?.start;
      filter.endDate = filter?.createdAt?.end
        ? filter?.createdAt?.end
        : new Date();
    }
    const queryParams = new URLSearchParams({
      ...filter,
      page: currentPage,
      limit: 10,
      sortField: sortConfig.key,
      sortOrder: sortConfig.direction,
    }).toString();
    
    await get(`/scope3/processingOfSoldProducts/get?${queryParams}`)
      .then((res) => {
        let arr = res?.data?.data?.map((item) => {
          const originalCreatedDate = item?.createdAt;
          const formattedCreatedDate = originalCreatedDate
            ? new Date(originalCreatedDate).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "-";

          const quantitySoldValue = item?.quantitySold?.value 
            ? `${item.quantitySold.value} ${item.quantitySold.unit || "KG"}`
            : "-";

          const electricityVal =
            item?.electricityConsumption ?? item?.energyConsumptionForProcessing;
          const energyConsumptionValue =
            electricityVal !== undefined && electricityVal !== null && electricityVal !== ""
              ? `${electricityVal} KWH`
              : "-";

          const fuelEntries = Array.isArray(item?.fuelEntries) ? item.fuelEntries : [];
          const fuelDisplay =
            fuelEntries.length > 0
              ? fuelEntries
                  .map((f) => {
                    const qty = f?.fuelConsumption ?? f?.quantity ?? "";
                    const unit = f?.fuelConsumptionUnit || f?.unit || "";
                    const name = f?.fuelType || f?.fuelName || "";
                    return qty ? `${name} ${qty} ${unit}`.trim() : name;
                  })
                  .filter(Boolean)
                  .join(", ")
              : item?.fuelType
                ? `${item.fuelType} ${item.fuelConsumption || ""} ${item.fuelConsumptionUnit || ""}`.trim()
                : item?.energySourceForProcessing || "-";

          return {
            ...item,
            createdAt: formattedCreatedDate,
            createdAtOriginal: originalCreatedDate,
            soldProductDate: item?.soldProductDate || originalCreatedDate,
            productId: item?.productId || item?.productIdSku || "-",
            productName: item?.productName || "-",
            vendorName: item?.vendorName || "-",
            quantitySoldDisplay: quantitySoldValue,
            quantitySold: item?.quantitySold || {},
            processingMethodUsed: item?.processingMethodUsed || "-",
            electricityConsumption: electricityVal ?? "",
            energyConsumptionForProcessing: electricityVal ?? "",
            energyDisplay: energyConsumptionValue,
            fuelDisplay,
            action: "View",
            id: item?._id,
          };
        });
        setTableData(arr);
        setTotalPages(res?.data?.totalPages);
      })
      .catch((err) => {
        console.log(err);
      });
  };

  useEffect(() => {
    fetchData();
  }, [sortConfig, filterObj, currentPage]);

  const fetchFilters = async () => {
    try {
      const res = await get(`/scope3/processingOfSoldProducts/filter`);
      const { productName, productCategory, vendorName, locationOfProcessing } =
        res?.data?.data || {};
      setFilters({
        productName: productName || [],
        productCategory: productCategory || [],
        vendorName: vendorName || [],
        locationOfProcessing: locationOfProcessing || [],
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchCardData = async () => {
    try {
      const res = await get(`/scope3/processingOfSoldProducts/cardData`);
      setCardData({
        totalProcessings: res?.data?.data?.totalProcessings || 0,
        topCategory: res?.data?.data?.topCategory || [],
        topVendor: res?.data?.data?.topVendor || null,
      });
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, []);

  const openModal = (type, dataForEdit) => {
    if (type === "add") {
      setIsModalOpen(true);
    } else if (type === "edit") {
      setIsModalOpen(true);
      setEditData(dataForEdit);
      setEditModal(true);
      setViewModal(false);
    }
  };

  const handleEdit = (data) => {
    setIsModalOpen(true);
    setEditData(data);
    setEditModal(true);
    setViewModal(false);
  };

  const openViewModal = (data) => {
    setEditData(data);
    setViewModal(true);
  };

  const handleSort = (key, direction) => {
    setSortConfig({ key, direction });
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleFilterChange = (newFilterObj) => {
    setFilterObj(newFilterObj);
    setCurrentPage(1);
  };

  const closeModal = (type) => {
    if (type === "add") {
      setIsModalOpen(false);
    } else if (type === "edit") {
      setIsModalOpen(false);
      setEditModal(false);
      setEditData({});
    }
  };

  const handleSubmit = async (data, type) => {
    try {
      if (type) {
        let res = await put(
          `/scope3/processingOfSoldProducts/update?Id=${editData._id}`,
          data
        );
        if (res) {
          setsnackOpen({
            open: true,
            message: res.data.message,
            severity: "success",
          });
          setIsModalOpen(false);
          setEditModal(false);
          setEditData({});
          fetchCardData();
        }
      } else {
        let res = await post(`/scope3/processingOfSoldProducts/add`, data);
        if (res) {
          setsnackOpen({
            open: true,
            message: res.data.message,
            severity: "success",
          });
          setIsModalOpen(false);
          fetchCardData();
        }
      }

      fetchData();
    } catch (error) {
      setsnackOpen({
        open: true,
        message: error?.response?.data?.message || "Something went wrong",
        severity: "error",
      });
      console.log("Error in handleSubmit:", error.response?.data?.message);
    }
  };

  const handleSelectChange = (value) => {
    setSelectedValue(value);
  };

  const handleDeleteOpen = (itemId) => {
    setDeletedItemId(itemId);
    setDeleteDialog(true);
  };

  const handleDeleteAggree = async () => {
    await del(`/scope3/processingOfSoldProducts/delete`, {
      params: {
        Id: deletedItemId?._id,
      },
    })
      .then(async (res) => {
        setDeleteDialog(false);
        setsnackOpen({
          open: true,
          message: res.data.message,
          severity: "success",
        });
        fetchData();
        fetchCardData();
      })
      .catch((err) => {
        console.log(err);
        setsnackOpen({
          open: true,
          message: err.response?.data?.message ?? "Network Error",
          severity: "error",
        });
      });
  };

  return (
    <>
      <Cards
        data={bannerData}
        title={"Processing of Sold Products"}
        onFilterChange={handleSelectChange}
      />
      <ActionButtons
        onFilterClick={() => setIsFilterModalOpen(true)}
        onAddClick={() => openModal("add")}
        filterObj={filterObj}
        onUploadClick={() => setBulkUploadModal(true)}
      />

      <ViewTable
        title={"Processing of Sold Products"}
        headers={headers}
        data={tableData ?? []}
        onSort={handleSort}
        sortConfig={sortConfig}
        page={currentPage}
        count={pageSize}
        editIcon={user?.companyAdmin ? "disable" : "enable"}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        onView={(row) => {
          openViewModal(row);
        }}
        onEdit={(row) => openModal("edit", row)}
        onDelete={(row) => handleDeleteOpen(row)}
      />
      <Snackbar
        open={snackOpen.open}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        onClose={() => setsnackOpen({ ...snackOpen, open: false })}
      >
        <Alert
          onClose={() => setsnackOpen({ ...snackOpen, open: false })}
          severity={snackOpen.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackOpen.message}
        </Alert>
      </Snackbar>

      <Form
        isOpen={isModalOpen}
        onClose={() => closeModal(editModal ? "edit" : "add")}
        onSubmit={handleSubmit}
        header={
          editModal
            ? "Edit Processing of Sold Product"
            : "Add Processing of Sold Product"
        }
        initialData={editData}
        isEditing={editModal}
      />

      <ViewData
        id={editData ? editData._id : ""}
        element={editData}
        title={"Processing of Sold Products"}
        viewData={viewModal}
        setViewData={setViewModal}
        setData={setEditData}
        inputData={inputData}
        handleEdit={handleEdit}
        companyAdmin={user?.companyAdmin}
      />

      {deleteDialog && (
        <DeleteAlertDialog
          {...{
            deleteDialog,
            setDeleteDialog,
            method: "delete",
            id: deletedItemId?._id,
            url: `${api}/scope3/processingOfSoldProducts/delete`,
            setsnackOpen,
            fetchData,
            fetchCardData,
          }}
        />
      )}

      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        filters={filterData}
        onFilterChange={handleFilterChange}
        title="Filter"
      />

      {bulkUploadModal && (
        <BulkUploadModal
          onCloseBulkUpload={() => {
            setBulkUploadModal(false);
            fetchData();
          }}
          isBulkUploadModal={bulkUploadModal}
          excelData={excelData}
          fileName={"processingOfSoldProducts"}
          setsnackOpen={setsnackOpen}
          url={"/scope3/processingOfSoldProducts/bulkupload"}
        />
      )}
    </>
  );
};

export default ProcessingOfSoldProducts;
